import { useState, useEffect } from "react";
import API from "../Api/Api";
import qs from "qs";
import { useTranslation } from "react-i18next";
import { otherLanguage } from "../utils/utils";
import { handleError, handleInfo } from "../REDUX/actions/notifications";
import { useDispatch } from "react-redux";
import _ from "lodash";
import { closeModal } from "../REDUX/actions/confirmationModal";

export function useProductsFR(fetchAll = false) {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const locale = i18n.language?.includes("en") ? "en" : "fr";
  const [products, setProducts] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const query = qs.stringify({
    sort: {
      id: "DESC",
    },
    populate: {
      localizations: {
        populate: "*",
      },
      seo: {
        populate: {
          ogimage: {
            populate: "*",
          },
        },
      },
      banner: { populate: "*" },
      introduction: { populate: "*" },
      informations: { populate: "*" },
      characteristics: { populate: "*" },
      minimalConsumption: { populate: { items: { populate: "*" } } },
      innovation: { populate: { items: { populate: "*" } } },
      innovationTabs: { populate: { items: { populate: "*" } } },
      description: { populate: "*" },
      hydrotherapySeats: {
        populate: {
          image: {
            populate: "*",
          },
          seat: {
            populate: {
              items: { populate: "*" },
            },
          },
        },
      },
      jets: { populate: { items: { populate: "*" } } },
      ergonomicDesign: { populate: { items: { populate: "*" } } },
      features: { populate: { items: { populate: "*" } } },
      structure: { populate: "*" },
      quality: { populate: { items: { populate: "*" } } },
      warranty: {
        populate: {
          image: { populate: "*" },
          items: { populate: "*" },
        },
      },
      optimalErgonomics: { populate: { items: { populate: "*" } } },
      customization: {
        populate: { panels: { populate: "*" }, otherPanels: { populate: "*" } },
      },
      factory: { populate: { items: { populate: "*" } } },
      partners: {
        populate: {
          partnerTabs: {
            populate: {
              items: { populate: "*" },
            },
          },
        },
      },
      files: {
        populate: {
          technicalFile: { populate: "*" },
        },
      },
    },
  });

  const fetchProducts = async () => {
    setFetching(true);
    try {
      const { data } = await API.get(
        `/api/fr-products?${query}&locale=${locale}`,
      );
      setProducts(data?.data);
    } catch (err) {
      console.error("Error fetching products:", err);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (fetchAll) {
      fetchProducts();
    }
  }, [fetchAll, locale]);

  const createProduct = async () => {
    setCreating(true);
    try {
      const defaultSEOFR = {
        title: "titre SEO produit par defaut",
        description: "SEO description produit par defaut",
      };
      const defaultSEOEN = {
        title: "titre SEO produit par defaut (en)",
        description: "SEO description produit par defaut (en)",
      };
      const { data: seoData } = await API.post(`/api/seos`, {
        data: {
          ...defaultSEOFR,
        },
      });
      const { data: seoDataEN } = await API.post(`/api/seos`, {
        data: {
          ...defaultSEOEN,
        },
      });
      const { data } = await API.post(`/api/fr-products?${query}`, {
        data: { seo: seoData?.data?.id },
      });

      const { data: dataEN } = await API.post(
        `/api/fr-products/${data.data.id}/localizations`,
        {
          locale: otherLanguage(locale),
          seo: seoDataEN?.data?.id,
        },
      );

      const { data: product } = await API.get(
        `/api/fr-products/${data?.data?.id}?${query}`,
      );

      setProducts((prev) => [
        locale === "en" ? dataEN : product?.data,
        ...prev,
      ]);

      dispatch(handleInfo("Produit Créé", 1));
    } catch (err) {
      console.error("Error creating product:", err);
    } finally {
      setCreating(false);
    }
  };

  const updateProduct = async (
    id,
    key,
    updates,
    type,
    collection,
    promoFields = {},
  ) => {
    setUpdating(true);
    const typeFields =
      key === "informations"
        ? {
            type,
            collection,
          }
        : {};
    try {
      const processNestedObjects = (obj, key) => {
        if (!obj || typeof obj !== "object") return obj;

        // Handle arrays
        if (Array.isArray(obj)) {
          return obj.map((item) => processNestedObjects(item, key));
        }

        // Process each property recursively
        const newObj = {};
        Object.keys(obj).forEach((key) => {
          if (obj[key] && typeof obj[key] === "object") {
            newObj[key] = processNestedObjects(obj[key], key);
          } else {
            newObj[key] = obj[key];
          }
        });

        // Remove id from the current object level
        if (
          [
            "image",
            "images",
            "file",
            "media",
            "technicalFile",
            "panelImage",
            "icon",
          ]?.includes(key)
        )
          return newObj;
        delete newObj.id;
        return newObj;
      };
      const { data } = await API.put(`/api/fr-products/${id}`, {
        data: {
          [key]: processNestedObjects(updates),
          ...typeFields,
          ...promoFields,
        },
      });
      setProducts((prev) =>
        prev.map((product) => (+product.id === +id ? data : product)),
      );
      dispatch(handleInfo("Produit Modifié", 1));
    } catch (err) {
      dispatch(handleError("Erreur de modification de produit", 1));
    } finally {
      setUpdating(false);
    }
  };

  const deleteProduct = async (id) => {
    setDeleting(true);
    try {
      await API.delete(`/api/fr-products/${id}`);
      setProducts((prev) => prev.filter((product) => +product.id !== +id));
      dispatch(handleInfo("Produit Supprimé", 1));
    } catch (err) {
      console.error("Error deleting product:", err);
    } finally {
      setDeleting(false);
    }
  };

  const getOneProduct = async (id) => {
    setFetching(true);
    try {
      const { data } = await API.get(
        `/api/fr-products/${id}?${query}&locale=${locale}`,
      );
      return data;
    } catch (err) {
      console.error("Error fetching product:", err);
      return null;
    } finally {
      setFetching(false);
    }
  };

  const duplicateProduct = async (id) => {
    setCreating(true);
    try {
      // Get the original product with all its relations
      const { data: originalProduct } = await API.get(
        `/api/fr-products/${id}?${query}&locale=${locale}`,
      );

      if (!originalProduct?.data) {
        throw new Error("Product not found");
      }

      const product = originalProduct.data;

      // Duplicate SEO first
      const { data: seoData } = await API.post(`/api/seos`, {
        data: {
          title: `${product.attributes.seo.title ?? ""} (copy)`,
          description: product.attributes.seo.description,
          ogimage: product.attributes.seo.ogimage?.data?.id,
        },
      });

      // Create new product with duplicated data
      let productData = {
        ...product.attributes,
        seo: seoData?.data?.id,
      };

      // Helper function to process nested objects
      const processNestedObjects = (obj) => {
        if (!obj || typeof obj !== "object") return obj;

        // Handle arrays
        if (Array.isArray(obj)) {
          return obj.map((item) => processNestedObjects(item));
        }

        // Handle data objects at any level
        if (Object.hasOwn(obj, "data")) {
          // If data is an array, process each item
          if (Array.isArray(obj.data)) {
            return obj.data.map((item) => item?.id ?? null);
          }
          // If data is an object, return its id
          else if (obj.data && typeof obj.data === "object") {
            return obj.data?.id ?? null;
          }
          // If data is neither array nor object, return as is
          return obj.data;
        }

        // Process each property recursively
        const newObj = {};
        Object.keys(obj).forEach((key) => {
          if (obj[key] && typeof obj[key] === "object") {
            newObj[key] = processNestedObjects(obj[key]);
          } else {
            newObj[key] = obj[key];
          }
        });

        // Remove id from the current object level
        delete newObj.id;
        return newObj;
      };

      // Remove ids from relations to create new ones
      delete productData.id;
      delete productData.createdAt;
      delete productData.updatedAt;
      delete productData.publishedAt;
      delete productData.localizations;

      // Process all nested objects in productData
      productData = processNestedObjects(productData);

      // Create the new product
      const { data: newProduct } = await API.post(`/api/fr-products`, {
        data: {
          ...productData,
          informations: {
            ...productData.informations,
            name: `${productData?.informations?.name ?? ""} (copy)`,
          },
          type:
            productData.type !== null && productData.type !== undefined
              ? `${productData.type}`
              : null,
          collection:
            productData.collection !== null &&
            productData.collection !== undefined
              ? `${productData.collection}`
              : null,
        },
      });

      // Handle localizations if they exist
      if (product.attributes.localizations?.data?.length > 0) {
        const localization = product.attributes.localizations.data[0];

        // Duplicate SEO for localization
        const { data: seoDataLocalized } = await API.post(`/api/seos`, {
          data: {
            title: `${localization.attributes.seo?.title ?? ""} (copy)`,
            description: localization.attributes.seo.description,
            ogimage: localization.attributes.seo.ogimage?.data?.id,
          },
        });

        // Create localization
        const localizationData = processNestedObjects(localization.attributes);
        await API.post(`/api/fr-products/${newProduct.data.id}/localizations`, {
          locale: otherLanguage(locale),
          ...localizationData,
          seo: seoDataLocalized?.data?.id,
          type: productData.type,
          collection: productData.collection,
        });
      }

      // Fetch the complete new product with all relations
      const { data: finalProduct } = await API.get(
        `/api/fr-products/${newProduct?.data?.id}?${query}`,
      );

      setProducts((prev) => [finalProduct?.data, ...prev]);
      dispatch(handleInfo("Produit dupliqué avec succès", 1));
    } catch (err) {
      console.error("Error duplicating product:", err);
      dispatch(handleInfo("Erreur lors de la duplication du produit", 2));
    } finally {
      setCreating(false);
    }
  };
  const showOrHideProductFR = async (ids, hide) => {
    try {
      for (const id of ids) {
        if (id !== null && id !== undefined) {
          await API.put(`api/fr-products/${id}`, { data: { hide: hide } });
        }
      }
      fetchProducts();
      // dispatch(getAllProducts());
      dispatch(closeModal());
      dispatch(handleInfo(hide ? "Produit caché" : "Produit affiché", 1));
    } catch (error) {}
  };

  return {
    products,
    fetching,
    creating,
    updating,
    deleting,
    createProduct,
    updateProduct,
    deleteProduct,
    getOneProduct,
    duplicateProduct,
    showOrHideProductFR,
  };
}
